import React, { useEffect, useId, useMemo, useReducer, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, IconButton, InputAdornment, List, ListItemButton, ListItemText,
  MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Building2, Copy, DoorOpen, Lock, Maximize, Minus, MousePointer2, Plus, Redo2, RectangleHorizontal,
  RotateCw, SquareDashed, Trash2, Undo2, Users, X,
} from 'lucide-react';
import { FloorplanArea, FloorplanAssignment, FloorplanDesk, FloorplanLevel } from '../../types/floorplan';
import { Kbd, SearchField, StatusPill } from '../ui';
import { focusRing, sbg } from '../../theme/sbgTokens';
import { formatCount, formatNumber, formatPercent } from '../../utils/format';
import { floorplanAreaTypeLabel, FLOORPLAN_AREA_TYPES, getFloorplanAreaAppearance } from './areaAppearance';
import {
  addObject, canvasPoint, changeGeometry, cloneLevel, createHistory, duplicateObject, EditorSelection,
  fitZoom, GRID_SIZE, layoutHistoryReducer, MAX_AREAS, MAX_DESKS, moveObject, PlacementTool, Rect,
  removeObject, resizeFloor, rotateObject, selectedObject, validateDraft,
} from './floorplanLayoutEditorState';

export interface FloorplanLayoutEditorProps {
  level: FloorplanLevel;
  assignmentsByDeskId: Map<string, FloorplanAssignment>;
  onSave: (level: FloorplanLevel) => Promise<void>;
  onClose: () => void;
}

type Tool = 'select' | PlacementTool;
const palette: { tool: Tool; label: string; icon: React.ElementType }[] = [
  { tool: 'select', label: 'Select and move', icon: MousePointer2 },
  { tool: 'desk', label: 'Desk', icon: RectangleHorizontal },
  { tool: 'office', label: 'Office', icon: Building2 },
  { tool: 'meeting_room', label: 'Meeting room', icon: Users },
  { tool: 'wall', label: 'Wall', icon: Minus },
  { tool: 'door', label: 'Door', icon: DoorOpen },
  { tool: 'open_area', label: 'Open area', icon: SquareDashed },
];
const areaTypes = FLOORPLAN_AREA_TYPES as readonly string[];
const typeLabel = floorplanAreaTypeLabel;
const objectName = (item: FloorplanDesk | FloorplanArea) => 'number' in item ? `Desk ${item.number}` : item.label;
const errorMessage = (error: unknown) => {
  const failure = error as { data?: { error?: string }; message?: string } | null;
  return failure?.data?.error || failure?.message || 'Couldn’t save the layout. Your draft is still here; try again in a minute.';
};

/** A section label inside a panel: the overline style, secondary text. */
const PanelLabel: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => (
  <Typography variant="overline" component="h2" sx={{ display: 'block', color: sbg.text.secondary, ...sx }}>
    {children}
  </Typography>
);

// Keep incomplete numeric typing local; only valid whole numbers enter geometry.
// On blur an incomplete or rejected edit returns to the last accepted value.
const NumberField: React.FC<{
  label: string; value: number; min: number; max: number; disabled: boolean; onChange: (value: number) => boolean;
}> = ({ label, value, min, max, disabled, onChange }) => {
  const [text, setText] = useState(String(value));
  const [invalid, setInvalid] = useState(false);
  useEffect(() => { setText(String(value)); setInvalid(false); }, [value]);
  return (
    <TextField
      label={label}
      type="number"
      value={text}
      disabled={disabled}
      size="small"
      fullWidth
      inputProps={{ min, max, step: 1 }}
      InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
      error={invalid}
      helperText={invalid ? `${formatNumber(min)}–${formatNumber(max)} px, whole numbers` : undefined}
      onChange={event => {
        const next = event.target.value;
        setText(next);
        const number = Number(next);
        const valid = next !== '' && Number.isInteger(number) && number >= min && number <= max;
        setInvalid(!valid || !onChange(number));
      }}
      onBlur={() => { setText(String(value)); setInvalid(false); }}
      sx={{ '& input': { fontVariantNumeric: 'tabular-nums' } }}
    />
  );
};

/** An icon button with its tooltip; the span keeps the tooltip working while disabled. */
const ToolbarIconButton: React.FC<{
  label: string; tooltip?: string; disabled?: boolean; onClick: () => void; children: React.ReactNode;
}> = ({ label, tooltip, disabled, onClick, children }) => (
  <Tooltip title={tooltip ?? label}>
    <span>
      <IconButton size="small" aria-label={label} disabled={disabled} onClick={onClick}>
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

interface DragState {
  pointerId: number;
  selection: EditorSelection;
  base: FloorplanLevel;
  origin: Rect;
  start: { x: number; y: number };
  latest: FloorplanLevel;
  mode: 'move' | 'resize';
}

const FloorplanLayoutEditor: React.FC<FloorplanLayoutEditorProps> = ({ level, assignmentsByDeskId, onSave, onClose }) => {
  const [history, dispatch] = useReducer(layoutHistoryReducer, level, createHistory);
  const [baseline, setBaseline] = useState(() => JSON.stringify(level));
  const [sourceSignature, setSourceSignature] = useState(() => JSON.stringify(level));
  const [selection, setSelection] = useState<EditorSelection | null>(null);
  const [tool, setTool] = useState<Tool>('select');
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState<'close' | 'reload' | null>(null);
  const [dragPreview, setDragPreview] = useState<FloorplanLevel | null>(null);
  const savingRef = useRef(false);
  const dragRef = useRef<DragState | null>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const canvasFocusRef = useRef<HTMLDivElement>(null);
  const sourceRef = useRef(level);
  sourceRef.current = level;
  const patternId = `editor-grid-${useId().replace(/:/g, '')}`;
  const draft = history.present;
  const rendered = dragPreview || draft;
  const dirty = JSON.stringify(draft) !== baseline;
  const snapshotChanged = JSON.stringify(level) !== sourceSignature;
  const validation = validateDraft(draft);
  const selected = selectedObject(rendered, selection);
  const assignedDeskIds = useMemo(() => new Set(assignmentsByDeskId.keys()), [assignmentsByDeskId]);
  const selectedAssignment = selection?.kind === 'desk' ? assignmentsByDeskId.get(selection.id) : undefined;
  const selectedIsAssigned = Boolean(selectedAssignment);
  const disabled = saving || Boolean(dragPreview);
  const objects = useMemo(() => [
    ...draft.desks.map(item => ({ selection: { kind: 'desk', id: item.id } as EditorSelection, item, label: objectName(item) })),
    ...draft.areas.map(item => ({ selection: { kind: 'area', id: item.id } as EditorSelection, item, label: objectName(item) })),
  ], [draft.desks, draft.areas]);
  const filteredObjects = objects.filter(object => object.label.toLowerCase().includes(search.trim().toLowerCase()));

  const fit = () => {
    const viewport = viewportRef.current;
    setZoom(fitZoom(draft, viewport?.clientWidth || 900, viewport?.clientHeight || 600));
    viewport?.scrollTo?.({ left: 0, top: 0 });
  };
  useEffect(() => {
    if (!dirty && !saving && !dragPreview) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, saving, dragPreview]);
  useEffect(() => {
    if (selection && !selectedObject(draft, selection)) setSelection(null);
  }, [draft, selection]);

  const commit = (next: FloorplanLevel) => {
    if (savingRef.current) return;
    dispatch({ type: 'commit', level: next });
    setError(null);
    setSaved(false);
  };
  const safely = (action: () => void) => {
    try { action(); return true; } catch (failure) { setError(errorMessage(failure)); return false; }
  };
  const reload = () => {
    dispatch({ type: 'reset', level });
    setBaseline(JSON.stringify(level));
    setSourceSignature(JSON.stringify(level));
    setSelection(null);
    setDragPreview(null);
    dragRef.current = null;
    setError(null);
    setSaved(false);
    setConfirmDiscard(null);
  };
  const requestClose = () => {
    if (savingRef.current) return;
    const activeDrag = dragRef.current;
    if (activeDrag) {
      commit(activeDrag.latest);
      dragRef.current = null;
      setDragPreview(null);
      if (svgRef.current?.hasPointerCapture?.(activeDrag.pointerId)) svgRef.current.releasePointerCapture(activeDrag.pointerId);
    }
    if (activeDrag ? JSON.stringify(activeDrag.latest) !== baseline : dirty) setConfirmDiscard('close');
    else onClose();
  };
  const save = async () => {
    if (savingRef.current || dragRef.current || !dirty || validation || snapshotChanged) return;
    const snapshot = cloneLevel(draft);
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      await onSave(snapshot);
      setBaseline(JSON.stringify(snapshot));
      setSourceSignature(JSON.stringify(sourceRef.current));
      dispatch({ type: 'reset', level: snapshot });
      setSaved(true);
      // The parent owns closing after a successful save and refreshing its API snapshot.
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const place = (point: { x: number; y: number }) => {
    if (tool === 'select' || disabled) return;
    safely(() => {
      const result = addObject(draft, tool, point, snapToGrid);
      commit(result.level);
      setSelection(result.selection);
    });
  };
  const chooseObject = (next: EditorSelection) => { setSelection(next); setTool('select'); };
  const removeSelected = () => {
    if (!selection || disabled) return;
    safely(() => { commit(removeObject(draft, selection, assignedDeskIds)); setSelection(null); });
  };
  const handleKeys = (event: React.KeyboardEvent) => {
    const target = event.target as HTMLElement;
    if (target.matches('input,textarea,select') || target.isContentEditable || disabled || confirmDiscard) return;
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      dispatch({ type: event.shiftKey ? 'redo' : 'undo' });
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
      event.preventDefault(); dispatch({ type: 'redo' }); return;
    }
    const delta: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    if (selection && selected && delta[event.key]) {
      event.preventDefault();
      const step = event.shiftKey || snapToGrid ? GRID_SIZE : 1;
      commit(changeGeometry(draft, selection, { x: selected.x + delta[event.key][0] * step, y: selected.y + delta[event.key][1] * step }));
    }
  };
  const pointFor = (event: React.PointerEvent) => canvasPoint(event.clientX, event.clientY, svgRef.current!.getBoundingClientRect(), zoom);
  const beginDrag = (event: React.PointerEvent, next: EditorSelection, mode: DragState['mode']) => {
    if (tool !== 'select' || savingRef.current || dragRef.current || event.button !== 0) return;
    event.preventDefault(); event.stopPropagation();
    const object = selectedObject(draft, next);
    if (!object) return;
    chooseObject(next);
    canvasFocusRef.current?.focus({ preventScroll: true });
    dragRef.current = { pointerId: event.pointerId, selection: next, base: draft, origin: object, start: pointFor(event), latest: draft, mode };
    svgRef.current?.setPointerCapture?.(event.pointerId);
  };
  const continueDrag = (event: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const point = pointFor(event);
    const dx = point.x - drag.start.x;
    const dy = point.y - drag.start.y;
    drag.latest = drag.mode === 'move'
      ? moveObject(drag.base, drag.selection, drag.origin.x + dx, drag.origin.y + dy, snapToGrid)
      : changeGeometry(drag.base, drag.selection, {
          width: Math.max(4, Math.min(draft.width - drag.origin.x, snapToGrid ? Math.round((drag.origin.width + dx) / GRID_SIZE) * GRID_SIZE : Math.round(drag.origin.width + dx))),
          height: Math.max(4, Math.min(draft.height - drag.origin.y, snapToGrid ? Math.round((drag.origin.height + dy) / GRID_SIZE) * GRID_SIZE : Math.round(drag.origin.height + dy))),
        });
    setDragPreview(drag.latest);
  };
  const endDrag = (event: React.PointerEvent, cancel = false) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (!cancel) commit(drag.latest);
    dragRef.current = null;
    setDragPreview(null);
    if (svgRef.current?.hasPointerCapture?.(event.pointerId)) svgRef.current.releasePointerCapture(event.pointerId);
  };
  const inputGeometry = (property: keyof Rect, value: number) => safely(() => { if (selection) commit(changeGeometry(draft, selection, { [property]: value })); });

  const activeTool = palette.find(item => item.tool === tool);
  const saveState = saving
    ? { tone: 'info' as const, label: 'Saving…' }
    : dirty
      ? { tone: 'warning' as const, label: 'Unsaved changes' }
      : saved
        ? { tone: 'success' as const, label: 'Saved' }
        : { tone: 'neutral' as const, label: 'No changes' };
  const panelSx = { bgcolor: sbg.bg.surface, minHeight: 0 };

  return (
    <Dialog open fullScreen disableEscapeKeyDown={saving} onClose={requestClose} aria-labelledby="layout-editor-title" onKeyDown={handleKeys} TransitionProps={{ onEntered: fit }} PaperProps={{ sx: { bgcolor: sbg.bg.canvas, borderRadius: 0, boxShadow: 'none' } }}>
      <Box component="header" sx={{ display: 'flex', alignItems: 'center', gap: 2, px: { xs: 2, md: 3 }, py: 1.5, bgcolor: sbg.bg.surface, borderBottom: `1px solid ${sbg.border.default}` }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" component="p" sx={{ display: 'block', color: sbg.text.secondary }}>Floor plan</Typography>
          <Typography id="layout-editor-title" variant="h3" component="h1" sx={{ color: sbg.text.primary }}>Edit floor layout</Typography>
          <Typography variant="body2" noWrap sx={{ color: sbg.text.secondary, mt: 0.25 }}>{level.name} · Physical layout shared by every seating version</Typography>
        </Box>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }} aria-live="polite">
          <StatusPill tone={saveState.tone}>{saveState.label}</StatusPill>
        </Box>
        <Tooltip title="Close layout editor">
          <span>
            <IconButton aria-label="Close layout editor" onClick={requestClose} disabled={saving}><X /></IconButton>
          </span>
        </Tooltip>
      </Box>

      <Stack direction="row" alignItems="center" useFlexGap flexWrap="wrap" spacing={1} sx={{ px: 2, py: 1, bgcolor: sbg.bg.surface, borderBottom: `1px solid ${sbg.border.subtle}` }}>
        <ToolbarIconButton label="Undo layout change" tooltip="Undo (Ctrl or ⌘ Z)" disabled={disabled || !history.past.length} onClick={() => dispatch({ type: 'undo' })}><Undo2 /></ToolbarIconButton>
        <ToolbarIconButton label="Redo layout change" tooltip="Redo (Ctrl or ⌘ Shift Z)" disabled={disabled || !history.future.length} onClick={() => dispatch({ type: 'redo' })}><Redo2 /></ToolbarIconButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
        <Box role="group" aria-label="Zoom" sx={{ display: 'inline-flex', alignItems: 'center', p: '1px', border: `1px solid ${sbg.border.default}`, borderRadius: sbg.radius.pill }}>
          <ToolbarIconButton label="Zoom out" disabled={disabled || zoom <= 0.05} onClick={() => setZoom(value => Math.max(0.05, value - 0.1))}><Minus /></ToolbarIconButton>
          <Tooltip title="Reset zoom to 100%">
            <Button size="small" variant="text" aria-label="Reset zoom to 100 percent" disabled={disabled} onClick={() => setZoom(1)} sx={{ minWidth: 56, px: 1, fontVariantNumeric: 'tabular-nums' }}>{formatPercent(zoom * 100)}</Button>
          </Tooltip>
          <ToolbarIconButton label="Zoom in" disabled={disabled || zoom >= 3} onClick={() => setZoom(value => Math.min(3, value + 0.1))}><Plus /></ToolbarIconButton>
        </Box>
        <Button size="small" variant="text" startIcon={<Maximize />} disabled={disabled} onClick={fit}>Fit floor</Button>
        <FormControlLabel sx={{ ml: 0.5, mr: 0 }} control={<Checkbox checked={snapToGrid} disabled={disabled} onChange={event => setSnapToGrid(event.target.checked)} />} label={`Snap to ${GRID_SIZE} px`} />
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
          {formatNumber(draft.desks.length)} of {formatCount(MAX_DESKS, 'desk')} · {formatNumber(draft.areas.length)} of {formatCount(MAX_AREAS, 'area')}
        </Typography>
      </Stack>

      {(snapshotChanged || error || validation) && (
        <Stack spacing={1} sx={{ px: 2, pt: 1.5 }}>
          {snapshotChanged && <Alert severity="warning" action={<Button color="inherit" size="small" disabled={saving} onClick={() => dirty ? setConfirmDiscard('reload') : reload()}>Reload latest</Button>}>This floor changed after the editor opened. Your draft is kept. Reload the latest layout before saving.</Alert>}
          {(error || validation) && <Alert severity="error" onClose={error ? () => setError(null) : undefined}>{error || validation}</Alert>}
        </Stack>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, overflow: { xs: 'auto', md: 'hidden' } }}>
        <Box component="aside" aria-label="Layout tools and objects" sx={{ ...panelSx, width: { xs: '100%', md: 224 }, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: { md: `1px solid ${sbg.border.default}` }, borderBottom: { xs: `1px solid ${sbg.border.default}`, md: 0 } }}>
          <PanelLabel sx={{ px: 2, pt: 2, pb: 1 }}>Build</PanelLabel>
          <Box sx={{ px: 1.25, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: '1fr' }, gap: 0.5 }}>
            {palette.map(({ tool: value, label, icon: Icon }) => {
              const active = tool === value;
              return (
                <Button
                  key={value}
                  variant="text"
                  size="small"
                  aria-pressed={active}
                  disabled={disabled}
                  onClick={() => setTool(value)}
                  startIcon={<Icon />}
                  sx={{
                    justifyContent: 'flex-start',
                    borderRadius: sbg.radius.md,
                    px: 1,
                    color: active ? sbg.text.accent : sbg.text.primary,
                    bgcolor: active ? sbg.bg.selected : 'transparent',
                    '& .MuiButton-startIcon': { color: active ? sbg.text.accent : sbg.text.secondary },
                    '&:hover': { bgcolor: active ? sbg.bg.selectedStrong : sbg.bg.hover },
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Box>
          {tool !== 'select' && <Button size="small" variant="outlined" disabled={disabled} onClick={() => place({ x: draft.width / 2, y: draft.height / 2 })} sx={{ mx: 1.25, mt: 1 }}>Place in center</Button>}
          <Divider sx={{ mt: 1.5 }} />
          <Box sx={{ p: 1.5 }}>
            <SearchField label="Find an object" value={search} onChange={setSearch} width="100%" />
          </Box>
          <List dense aria-label="Floor objects" sx={{ flex: { xs: '0 0 auto', md: 1 }, minHeight: 80, maxHeight: { xs: 180, md: 'none' }, overflowY: 'auto', py: 0, pb: 1 }}>
            {filteredObjects.map(object => {
              const assigned = object.selection.kind === 'desk' && assignedDeskIds.has(object.item.id);
              return (
                <ListItemButton key={object.item.id} component="button" disabled={disabled} selected={selection?.id === object.item.id} aria-pressed={selection?.id === object.item.id} aria-label={`Select ${object.label}`} onClick={() => chooseObject(object.selection)} sx={{ width: 'calc(100% - 16px)', mx: 1, my: 0.25, py: 0.5, px: 1, gap: 1 }}>
                  <ListItemText
                    primary={object.label}
                    secondary={object.selection.kind === 'desk' ? assigned ? 'Desk · assigned' : 'Desk · open' : typeLabel((object.item as FloorplanArea).type)}
                    primaryTypographyProps={{ variant: 'labelSm', noWrap: true, sx: { color: sbg.text.primary } }}
                    secondaryTypographyProps={{ variant: 'caption', sx: { color: sbg.text.secondary } }}
                  />
                  {assigned && <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', color: sbg.text.secondary }}><Lock size={14} /></Box>}
                </ListItemButton>
              );
            })}
            {!filteredObjects.length && (
              <Typography variant="body2" sx={{ display: 'block', px: 2, py: 1, color: sbg.text.secondary }}>
                {objects.length ? `No objects match “${search.trim()}”.` : 'Place your first desk or area.'}
              </Typography>
            )}
          </List>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, minHeight: { xs: 360, md: 0 }, display: 'flex', flexDirection: 'column' }}>
          <Box ref={viewportRef} sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3, bgcolor: sbg.bg.canvas }}>
            <Box ref={canvasFocusRef} role="group" aria-label="Floor layout canvas" aria-describedby="layout-editor-help" tabIndex={0} sx={{ width: draft.width * zoom, height: draft.height * zoom, outline: 'none', borderRadius: '2px', '&:focus-visible': { ...focusRing, outlineOffset: 4 } }}>
              <svg
                ref={svgRef}
                width={draft.width * zoom}
                height={draft.height * zoom}
                viewBox={`0 0 ${draft.width} ${draft.height}`}
                aria-hidden="true"
                data-testid="layout-editor-canvas"
                style={{ display: 'block', background: sbg.bg.surface, outline: `1px solid ${sbg.border.default}`, cursor: tool === 'select' ? 'default' : 'crosshair', touchAction: 'none', userSelect: 'none', fontFamily: sbg.font.body }}
                onPointerDown={event => { if (event.button === 0 && !savingRef.current) { if (tool === 'select') setSelection(null); else place(pointFor(event)); } }}
                onPointerMove={continueDrag}
                onPointerUp={event => endDrag(event)}
                onPointerCancel={event => endDrag(event, true)}
                onLostPointerCapture={event => endDrag(event, true)}
              >
                <defs><pattern id={patternId} width={GRID_SIZE} height={GRID_SIZE} patternUnits="userSpaceOnUse"><path d={`M ${GRID_SIZE} 0 L 0 0 0 ${GRID_SIZE}`} fill="none" style={{ stroke: sbg.border.subtle }} strokeWidth={0.7} /></pattern></defs>
                <rect width={draft.width} height={draft.height} fill={`url(#${patternId})`} />
                {rendered.areas.map(area => {
                  const appearance = getFloorplanAreaAppearance(area.type);
                  const active = selection?.kind === 'area' && selection.id === area.id;
                  return <g key={area.id} data-testid={`layout-object-${area.id}`} onPointerDown={event => beginDrag(event, { kind: 'area', id: area.id }, 'move')} style={{ cursor: tool === 'select' ? 'move' : 'crosshair' }}>
                    <rect x={area.x} y={area.y} width={area.width} height={area.height} rx={appearance.borderRadius} strokeWidth={active ? 2 : appearance.strokeWidth} strokeDasharray={!active && appearance.dashed ? '6 4' : undefined} style={{ fill: appearance.background, stroke: active ? sbg.border.selected : appearance.stroke }} />
                    {!appearance.hideLabel && area.width > 45 && area.height > 18 && <text x={area.x + 8} y={area.y + (appearance.labelAtTop ? 20 : area.height / 2 + 4)} fontSize={12} fontWeight={500} pointerEvents="none" style={{ fill: appearance.labelColor }}>{area.label.length > Math.floor(area.width / 7) ? `${area.label.slice(0, Math.max(3, Math.floor(area.width / 7) - 2))}…` : area.label}</text>}
                  </g>;
                })}
                {rendered.desks.map(desk => {
                  const active = selection?.kind === 'desk' && selection.id === desk.id;
                  const occupied = assignedDeskIds.has(desk.id);
                  return <g key={desk.id} data-testid={`layout-object-${desk.id}`} onPointerDown={event => beginDrag(event, { kind: 'desk', id: desk.id }, 'move')} style={{ cursor: tool === 'select' ? 'move' : 'crosshair' }}>
                    <rect x={desk.x} y={desk.y} width={desk.width} height={desk.height} rx={Math.min(6, desk.width / 4, desk.height / 4)} strokeWidth={active ? 2 : 1} strokeDasharray={!active && !occupied ? '4 3' : undefined} style={{ fill: active ? sbg.bg.selected : sbg.bg.surface, stroke: active ? sbg.border.selected : sbg.border.strong }} />
                    {desk.width > 20 && desk.height > 15 && <text x={desk.x + desk.width / 2} y={desk.y + desk.height / 2 + 4} textAnchor="middle" fontSize={12} fontWeight={500} pointerEvents="none" style={{ fill: occupied || active ? sbg.text.primary : sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>{desk.number.slice(0, Math.max(2, Math.floor(desk.width / 7)))}</text>}
                    {occupied && <circle cx={desk.x + 6} cy={desk.y + 6} r={2.5} pointerEvents="none" style={{ fill: sbg.action.primary }} />}
                  </g>;
                })}
                {selected && selection && <g>
                  <rect x={selected.x - 3} y={selected.y - 3} width={selected.width + 6} height={selected.height + 6} rx={4} fill="none" strokeWidth={1} strokeDasharray="4 3" pointerEvents="none" style={{ stroke: sbg.border.selected }} />
                  <rect data-testid="layout-resize-handle" x={selected.x + selected.width - 5 / zoom} y={selected.y + selected.height - 5 / zoom} width={10 / zoom} height={10 / zoom} rx={2 / zoom} strokeWidth={1.5 / zoom} style={{ cursor: 'nwse-resize', fill: sbg.bg.surface, stroke: sbg.border.selected }} onPointerDown={event => beginDrag(event, selection, 'resize')} />
                </g>}
              </svg>
            </Box>
          </Box>
          <Box sx={{ px: 2, py: 1, bgcolor: sbg.bg.surface, borderTop: `1px solid ${sbg.border.subtle}` }}>
            <Typography id="layout-editor-help" variant="caption" component="p" sx={{ color: sbg.text.secondary, display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.5, rowGap: 0.25 }}>
              {tool === 'select'
                ? <>Drag to move; use the corner handle to resize. Select an object, then use arrow keys to nudge. <Kbd>Shift</Kbd> moves 10 px.</>
                : `Click the floor to place ${activeTool?.label.toLowerCase()}. Use Select and move to reposition.`}
              {' '}Scroll to pan.
            </Typography>
          </Box>
        </Box>

        <Box component="aside" aria-label="Layout inspector" sx={{ ...panelSx, width: { xs: '100%', md: 296 }, flexShrink: 0, overflowY: 'auto', borderLeft: { md: `1px solid ${sbg.border.default}` }, borderTop: { xs: `1px solid ${sbg.border.default}`, md: 0 }, p: 2.5 }}>
          <PanelLabel sx={{ mb: 1.5 }}>Floor</PanelLabel>
          <TextField label="Floor name" fullWidth size="small" value={draft.name} disabled={disabled} inputProps={{ maxLength: 120 }} onChange={event => commit({ ...draft, name: event.target.value })} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 2 }}>
            <NumberField label="Floor width" value={draft.width} min={100} max={10000} disabled={disabled} onChange={value => safely(() => commit(resizeFloor(draft, value, draft.height)))} />
            <NumberField label="Floor height" value={draft.height} min={100} max={10000} disabled={disabled} onChange={value => safely(() => commit(resizeFloor(draft, draft.width, value)))} />
          </Box>
          <Divider sx={{ my: 2.5 }} />
          <PanelLabel sx={{ mb: 1.5 }}>Selected object</PanelLabel>
          {selected && selection ? <Stack spacing={2}>
            {selection.kind === 'desk' ? (
              <TextField label="Desk number" value={(selected as FloorplanDesk).number} disabled={disabled} size="small" inputProps={{ maxLength: 40 }} onChange={event => commit({ ...draft, desks: draft.desks.map(item => item.id === selection.id ? { ...item, number: event.target.value } : item) })} />
            ) : <>
              <TextField label="Area label" value={(selected as FloorplanArea).label} disabled={disabled} size="small" inputProps={{ maxLength: 120 }} onChange={event => commit({ ...draft, areas: draft.areas.map(item => item.id === selection.id ? { ...item, label: event.target.value } : item) })} />
              <TextField select label="Area type" value={(selected as FloorplanArea).type} disabled={disabled} size="small" onChange={event => commit({ ...draft, areas: draft.areas.map(item => item.id === selection.id ? { ...item, type: event.target.value } : item) })}>
                {areaTypes.map(type => <MenuItem key={type} value={type}>{typeLabel(type)}</MenuItem>)}
              </TextField>
            </>}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <NumberField label="X position" value={selected.x} min={0} max={draft.width - selected.width} disabled={disabled} onChange={value => inputGeometry('x', value)} />
              <NumberField label="Y position" value={selected.y} min={0} max={draft.height - selected.height} disabled={disabled} onChange={value => inputGeometry('y', value)} />
              <NumberField label="Object width" value={selected.width} min={4} max={draft.width - selected.x} disabled={disabled} onChange={value => inputGeometry('width', value)} />
              <NumberField label="Object height" value={selected.height} min={4} max={draft.height - selected.y} disabled={disabled} onChange={value => inputGeometry('height', value)} />
            </Box>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button size="small" variant="outlined" disabled={disabled} startIcon={<RotateCw />} onClick={() => safely(() => commit(rotateObject(draft, selection)))}>Rotate 90°</Button>
              <Button size="small" variant="outlined" disabled={disabled} startIcon={<Copy />} onClick={() => safely(() => { const result = duplicateObject(draft, selection); commit(result.level); setSelection(result.selection); })}>Duplicate</Button>
            </Stack>
            {selectedIsAssigned && <Alert severity="info" role="note" icon={<Lock />}>Assigned to {selectedAssignment?.employee.name}. You can move or resize this desk; clear its assignment before removing it.</Alert>}
            <Button variant="outlined" color="error" startIcon={<Trash2 />} disabled={disabled || selectedIsAssigned} onClick={removeSelected} sx={{ alignSelf: 'flex-start' }}>Remove {selection.kind === 'desk' ? 'desk' : 'area'}</Button>
          </Stack> : <Typography variant="body1" sx={{ color: sbg.text.secondary }}>Choose an object on the floor or in the list to edit its position, size and label.</Typography>}
          <Divider sx={{ my: 2.5 }} />
          <Typography variant="body2" sx={{ color: sbg.text.secondary }}>Assignments stay with existing desks. Changes affect every seating version, and a desk assigned in any version can’t be removed. Rooms, doors and walls may overlap to form your layout.</Typography>
        </Box>
      </Box>

      <DialogActions sx={{ flexWrap: 'wrap', rowGap: 1, px: { xs: 2, md: 3 }, py: 1.5, bgcolor: sbg.bg.surface, borderTop: `1px solid ${sbg.border.default}` }}>
        <Typography variant="body2" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums', flexBasis: { xs: '100%', sm: 'auto' }, mr: { xs: 0, sm: 'auto' } }}>{formatNumber(draft.width)} × {formatNumber(draft.height)} px · Editing this floor only</Typography>
        <Button variant="text" onClick={requestClose} disabled={saving}>{dirty ? 'Discard changes' : 'Close'}</Button>
        <Button variant="contained" onClick={save} disabled={disabled || !dirty || Boolean(validation) || snapshotChanged} aria-busy={saving || undefined} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>{saving ? 'Saving layout…' : 'Save layout'}</Button>
      </DialogActions>

      <Dialog open={Boolean(confirmDiscard)} onClose={() => setConfirmDiscard(null)} aria-labelledby="discard-layout-title" maxWidth="xs" fullWidth>
        <DialogTitle id="discard-layout-title">Discard unsaved layout changes?</DialogTitle>
        <DialogContent><Typography variant="body1" sx={{ color: sbg.text.secondary }}>Your changes to desk positions, rooms and floor dimensions haven’t been saved. Discarding removes them.</Typography></DialogContent>
        <DialogActions>
          <Button variant="text" autoFocus onClick={() => setConfirmDiscard(null)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={() => { if (confirmDiscard === 'reload') reload(); else { setConfirmDiscard(null); onClose(); } }}>{confirmDiscard === 'reload' ? 'Discard and reload' : 'Discard and close'}</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default FloorplanLayoutEditor;
